import React from 'react';
import PropTypes from 'prop-types';
import moize from 'moize';
import { Field } from 'redux-form';
import { filter } from 'lodash';
import { TabPane } from 'reactstrap';
import { useIsExtensionInstalled, useHasRights, useAppKeyWords } from 'hooks';
import PermissionDenied from 'shared/permissionDenied';
import { TYPE_REGULAR } from 'enums/projectEnum';
import { UNASSIGNED } from 'enums/extensionShortIdEnum';
import styled from 'styled-components';
import { Divider, Layout, Typography, SelectReduxFormWrapper } from '@hub-mono/ui';

const filterProjectGroups = projectGroups =>
    filter(projectGroups, projectGroup => 'USER' === projectGroup.groupType && !projectGroup.isSmart);
let filteredProjectGroups = [];

const rights = [
    {
        rights: ['settingProjectRes'],
        rule: 'one',
        name: 'hasSettingProjectRes',
    },
    {
        rights: ['manageUnassignedRows'],
        rule: 'one',
        name: 'hasManageUnassignedRows',
    },
    {
        rights: ['settingProjectGroups'],
        rule: 'one',
        name: 'hasSettingProjectGroups',
    },
];

const StyledLayout = styled(Layout)`
    padding: 1.5rem 0.6rem 1.5rem;
`;

const ResourceAndGroupsTab = ({
    tabId,
    resources,
    projectGroups,
    unassignedWork,
    projectKeyWord,
    showProjectGroups,
    type,
}) => {
    const { resourcePluralKeyWord } = useAppKeyWords();
    const isUnassignedExtensionInstalled = useIsExtensionInstalled(UNASSIGNED);
    const { hasSettingProjectRes, hasManageUnassignedRows, hasSettingProjectGroups } = useHasRights(rights);
    if (showProjectGroups) {
        filteredProjectGroups = moize(filterProjectGroups, { maxSize: 2 });
    }

    const showAddProjectsToGroupsSection = showProjectGroups && type === TYPE_REGULAR.value && hasSettingProjectGroups;

    return (
        <TabPane tabId={tabId}>
            {hasSettingProjectRes ? (
                <>
                    <StyledLayout gap="--Spacing-150" stack>
                        <Typography variant="label" size="large" prominent>
                            Add {resourcePluralKeyWord}
                        </Typography>
                        <Field
                            dataCy="project-resources"
                            name="resources"
                            component={SelectReduxFormWrapper}
                            multiple
                            caseSensitive={false}
                            optionValue="_id"
                            filter="contains"
                            optionLabel={item => (item.firstName ? `${item.firstName} ${item.lastName}` : '')}
                            options={resources}
                            helperText={`Choose which ${resourcePluralKeyWord.toLowerCase()} belong to this ${projectKeyWord.toLowerCase()}`}
                        />
                    </StyledLayout>
                    <Divider />
                    {hasManageUnassignedRows && isUnassignedExtensionInstalled && (
                        <>
                            <StyledLayout gap="--Spacing-150" stack>
                                <Typography variant="label" size="large" prominent>
                                    Add unassigned work To {projectKeyWord.toLowerCase()}
                                </Typography>
                                <Field
                                    dataCy="project-unassigned-work"
                                    name="unassignedWork"
                                    component={SelectReduxFormWrapper}
                                    multiple
                                    caseSensitive={false}
                                    optionValue="_id"
                                    filter="contains"
                                    optionLabel={item => (Object.keys(item).length ? item.firstName : '')}
                                    options={unassignedWork}
                                    helperText={`Add unassigned work to this ${projectKeyWord.toLowerCase()}`}
                                />
                            </StyledLayout>
                            <Divider />
                        </>
                    )}
                </>
            ) : null}
            {showAddProjectsToGroupsSection ? (
                <StyledLayout gap="--Spacing-150" stack>
                    <Typography variant="label" size="large">
                        Add {projectKeyWord.toLowerCase()} To groups:
                    </Typography>
                    <Field
                        dataCy="project-groups"
                        name="projectGroups"
                        multiple
                        component={SelectReduxFormWrapper}
                        caseSensitive={false}
                        dropUp={true}
                        optionValue="_id"
                        filter="contains"
                        optionLabel="name"
                        options={filteredProjectGroups(projectGroups)}
                        helperText={`Choose which groups belong to this ${projectKeyWord.toLowerCase()}`}
                    />
                </StyledLayout>
            ) : null}
            {!hasSettingProjectRes && !showAddProjectsToGroupsSection ? <PermissionDenied /> : null}
        </TabPane>
    );
};

ResourceAndGroupsTab.propTypes = {
    tabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    projectKeyWord: PropTypes.string,
    type: PropTypes.string,
    unassignedWork: PropTypes.array,
    resources: PropTypes.array,
    projectGroups: PropTypes.array,
    showProjectGroups: PropTypes.bool,
};

ResourceAndGroupsTab.defaultProps = {
    projectKeyWord: 'Project',
    type: TYPE_REGULAR.value,
    unassignedWork: [],
    resources: [],
    projectGroups: [],
    showProjectGroups: true,
};

export default ResourceAndGroupsTab;
